import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wand2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { REGION_LABELS } from './ExerciseMetaForm';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const opts = { withCredentials: true };

// Survey vocabulary (backend/generator_api.py Survey)
const PATHS = [
  ['comfort', 'Komfort i swoboda ruchu'], ['function', 'Sprawność i funkcja ruchowa'],
  ['strength_physique', 'Siła i sylwetka'], ['conditioning', 'Kondycja'], ['longevity', 'Sprawność na lata'],
];
const ACCENTS = [['muscle', 'Mięśnie i sylwetka'], ['strength', 'Większa siła'], ['fat_loss', 'Mniej tkanki tłuszczowej']];
const EXPERIENCE = [['starting', 'Dopiero zaczyna'], ['returning', 'Wraca po przerwie'], ['lt_1y', 'Regularnie do roku'], ['gt_1y', 'Regularnie ponad rok']];
const EQUIPMENT = [['bands', 'Gumy'], ['dumbbells', 'Hantle'], ['kettlebell', 'Kettlebell'], ['pullup_bar', 'Drążek'], ['bench', 'Ławka / stopień']];
const ACTIVITIES = [['sitting', 'Siedzenie'], ['bending', 'Schylanie'], ['standing_up', 'Wstawanie'], ['walking', 'Chodzenie'], ['lifting', 'Dźwiganie'], ['overhead', 'Ręce nad głową']];
const SEVERITY = [['mild', 'Lekkie'], ['moderate', 'Umiarkowane'], ['severe', 'Silne']];

const chip = (on) => `text-sm px-3 py-1.5 rounded-full border ${on ? 'border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/10' : 'border-[#2A2A2A] text-[#A1A1A1]'}`;
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

const Choice = ({ label, options, value, onChange, multi }) => (
  <div className="space-y-2">
    <div className="label-text text-[#A1A1A1] text-xs">{label}</div>
    <div className="flex flex-wrap gap-2">
      {options.map(([v, l]) => {
        const on = multi ? value.includes(v) : value === v;
        return (
          <button key={String(v)} type="button" className={chip(on)} aria-pressed={on}
            onClick={() => onChange(multi ? toggle(value, v) : v)}>{l}</button>
        );
      })}
    </div>
  </div>
);

// Short form -> draft plan (status "draft", nothing reaches the client) -> PlanBuilder.
export const GeneratorDraftDialog = ({ open, onOpenChange, client, onCreated }) => {
  const [f, setF] = useState({
    path: 'function', accent: 'muscle', experience: 'lt_1y', location: 'gym', equipment: [],
    days_per_week: 3, session_minutes: 45, hasComplaint: false, regions: [], hard_activities: [],
    severity: 'mild', redFlag: false,
  });
  const [busy, setBusy] = useState(false);
  const [consult, setConsult] = useState(null);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));

  const generate = async () => {
    setBusy(true);
    setConsult(null);
    try {
      const survey = {
        path: f.path, accent: f.path === 'strength_physique' ? f.accent : null, experience: f.experience,
        location: f.location, equipment: f.location === 'home' ? f.equipment : [],
        days_per_week: f.days_per_week, session_minutes: f.session_minutes,
        complaints: f.hasComplaint ? { regions: f.regions, hard_activities: f.hard_activities, severity: f.severity } : null,
        red_flags: f.redFlag ? ['screening'] : [], age: client?.age || null,
      };
      const res = await axios.post(`${API_URL}/api/plan-generator/draft`, { client_id: client.id, survey }, opts);
      if (res.data.status === 'draft') {
        toast.success('Szkic gotowy. Przejrzyj go i popraw przed aktywacją.');
        onCreated(res.data.plan_id);
      } else {
        setConsult(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail?.toString?.() || 'Nie udało się wygenerować szkicu');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading-text text-2xl text-white">Szkic z generatora</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#A1A1A1]">Generator ułoży wersję roboczą planu. Klient nic nie zobaczy, dopóki nie aktywujesz planu.</p>
        <div className="space-y-5 mt-2" data-testid="generator-form">
          <Choice label="ŚCIEŻKA" options={PATHS} value={f.path} onChange={(v) => set({ path: v })} />
          {f.path === 'strength_physique' && <Choice label="AKCENT" options={ACCENTS} value={f.accent} onChange={(v) => set({ accent: v })} />}
          <Choice label="DOŚWIADCZENIE" options={EXPERIENCE} value={f.experience} onChange={(v) => set({ experience: v })} />
          <Choice label="MIEJSCE" options={[['gym', 'Siłownia'], ['home', 'Dom']]} value={f.location} onChange={(v) => set({ location: v })} />
          {f.location === 'home' && <Choice label="SPRZĘT W DOMU (brak zaznaczenia = tylko masa ciała)" options={EQUIPMENT} value={f.equipment} multi onChange={(v) => set({ equipment: v })} />}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Choice label="DNI W TYGODNIU" options={[[2, '2'], [3, '3'], [4, '4'], [5, '5']]} value={f.days_per_week} onChange={(v) => set({ days_per_week: v })} />
            <Choice label="CZAS TRENINGU" options={[[30, '30 min'], [45, '45 min'], [60, '60+ min']]} value={f.session_minutes} onChange={(v) => set({ session_minutes: v })} />
          </div>
          <Choice label="DOLEGLIWOŚCI" options={[[false, 'Nie'], [true, 'Tak']]} value={f.hasComplaint} onChange={(v) => set({ hasComplaint: v })} />
          {f.hasComplaint && (
            <div className="space-y-4 pl-3 border-l border-[#2A2A2A]">
              <Choice label="OKOLICA" options={Object.entries(REGION_LABELS)} value={f.regions} multi onChange={(v) => set({ regions: v })} />
              <Choice label="CO SPRAWIA TRUDNOŚĆ" options={ACTIVITIES} value={f.hard_activities} multi onChange={(v) => set({ hard_activities: v })} />
              <Choice label="NASILENIE" options={SEVERITY} value={f.severity} onChange={(v) => set({ severity: v })} />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-white">
            <input type="checkbox" checked={f.redFlag} onChange={(e) => set({ redFlag: e.target.checked })} />
            Odpowiedź „tak” w pytaniach bezpieczeństwa (ból w klatce, omdlenia, nieleczone nadciśnienie, ciąża / operacja, drętwienie)
          </label>
          {consult && (
            <div className="flex gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm" data-testid="generator-consult">
              <AlertTriangle className="w-5 h-5 shrink-0" /> <span>{consult}</span>
            </div>
          )}
          <Button onClick={generate} disabled={busy} className="bg-[#C9A84C] hover:bg-[#B89A3E] text-black rounded-full" data-testid="generator-submit">
            <Wand2 className="w-4 h-4 mr-2" /> {busy ? 'Generowanie...' : 'Wygeneruj szkic'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Shown above a generator draft in the PlanBuilder: what the generator took into
// account, gaps it could not fill, and the trainer's acceptance count so far.
export const GeneratorDraftInfo = ({ planId }) => {
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/plan-generator/plan/${planId}`, opts).then((r) => setInfo(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/plan-generator/stats`, opts).then((r) => setStats(r.data)).catch(() => {});
  }, [planId]);
  if (!info) return null;
  return (
    <div className="card-surface rounded-3xl p-6 mb-8 space-y-3" data-testid="generator-draft-info">
      <div className="flex items-center gap-2 text-[#C9A84C]">
        <Wand2 className="w-5 h-5" />
        <span className="heading-text text-lg">Szkic z generatora — wersja robocza, klient jej nie widzi</span>
      </div>
      {info.week?.length > 0 && <p className="text-sm text-white">Tydzień: {info.week.join(' · ')}</p>}
      {info.cycle && (
        <p className="text-sm text-white" data-testid="generator-cycle">
          Cykl: {info.cycle.weeks_total} tygodni
          {info.cycle.deload_weeks?.length > 0 && <> · tydzień lżejszy: {info.cycle.deload_weeks.join(', ')}</>}
          {' '}· przegląd co 2 tygodnie
        </p>
      )}
      {info.explanation?.map((t, i) => <p key={i} className="text-sm text-[#A1A1A1]">{t}</p>)}
      {info.warnings?.map((t, i) => (
        <p key={i} className="text-sm text-amber-300 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{t}</p>
      ))}
      {info.review ? (
        <p className="text-sm text-emerald-300 flex gap-2"><CheckCircle2 className="w-4 h-4" />
          Aktywowany: {info.review.changes} zmian ćwiczeń — {info.review.accepted ? 'przyjęty bez większych poprawek' : 'z większymi poprawkami'}</p>
      ) : (
        <p className="text-xs text-[#A1A1A1]">Popraw, co trzeba, i kliknij „Aktywuj plan”. Do {stats?.max_changes ?? 2} podmienionych, dodanych lub usuniętych ćwiczeń liczy się jako przyjęty bez większych poprawek.</p>
      )}
      {stats && <p className="text-xs text-[#A1A1A1]">Dotychczas: {stats.accepted} z {stats.activated} aktywowanych szkiców przyjętych bez większych poprawek (cel: 8 z 10).</p>}
    </div>
  );
};
